import { Flex, Select, Stack } from "@mantine/core";
import { type JSX, useState } from "react";

import type { Customer } from "@/customer-info";
import type { Feature, Product } from "@/customer-info/api";
import type { StepProps } from "@/customer-panel/components/call-intro-flow/steps/models";

import { useVerifiedCustomerStore } from "@/customer-info";
import { productMappings, products } from "@/customer-info/api";
import { createCase } from "@/customer-info/api/createCase";
import { Header } from "@/customer-panel/components/call-intro-flow/steps/Header";
import { NavButtons } from "@/customer-panel/components/call-intro-flow/steps/NavButtons";
import { useLogger, useSessionId } from "@/sdk";

import classes from "./ProductCollectionStep.module.css";

export function ProductCollectionStep({ onNext, onPrev }: StepProps): JSX.Element {
    const logger = useLogger({ module: "ProductCollectionStep" });
    const sessionId = useSessionId();
    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);

    /* We use this for case persistence for a contact with a customer
        for connect + salesforce, this is the contact id which we also use as our session id
        for twilio... callId changes on callbacks and call id would not work
        using session id for now, because it would work for both scenarios..
        but if session id was not derived from contact id in connect.. it may not work as consistently */
    const callId = useSessionId();

    const [selectedProduct, setSelectedProduct] = useState<Product | null>(customer?.metadata?.product ?? null);
    const [selectedFeature, setSelectedFeature] = useState<Feature | null>(customer?.metadata?.feature ?? null);
    const [isLoading, setIsLoading] = useState(false);

    const onNextClick = async (customer: Customer | undefined, product: Product, feature: Feature) => {
        setIsLoading(true);
        try {
            const caseDetails = customer
                ? await createCase(logger, {
                      email: customer.email,
                      firstName: customer.firstName,
                      lastName: customer.lastName,
                      feature,
                      product,
                      callId,
                  })
                : undefined;

            setIsLoading(false);

            useVerifiedCustomerStore.getState().setCustomer(sessionId, {
                ...customer,
                metadata: {
                    ...customer?.metadata,
                    product,
                    feature,
                    caseId: caseDetails?.caseId,
                    caseNumber: caseDetails?.caseNumber,
                },
            });
            onNext?.();
        } catch {
            logger.warn("Error creating case");
            setIsLoading(false);
        }
    };

    return (
        <Flex mt="sm" direction="column">
            <Header text="Which product and feature is the customer calling about?" />
            <Stack mt="md" mb="1.5rem">
                <Select
                    data-testid="select-feature"
                    classNames={{ input: classes.styledSelect }}
                    radius="sm"
                    value={selectedProduct}
                    placeholder="Product"
                    onChange={(val) => {
                        setSelectedFeature(null);
                        setSelectedProduct(val as Product);
                    }}
                    data={products}
                />
                <Select
                    data-testid="select-product"
                    classNames={{ input: classes.styledSelect }}
                    value={selectedFeature}
                    radius="sm"
                    placeholder="Feature"
                    onChange={(val) => setSelectedFeature(val as Feature)}
                    disabled={selectedProduct === null}
                    data={selectedProduct ? productMappings[selectedProduct] : []}
                />
            </Stack>
            <NavButtons
                isLoading={isLoading}
                onNext={
                    onNext && selectedProduct && selectedFeature
                        ? () => void onNextClick(customer, selectedProduct, selectedFeature)
                        : undefined
                }
                onPrev={onPrev}
            />
        </Flex>
    );
}
